import { useEffect } from "react"
import { useSelector } from "react-redux"
import { getFormFieldData } from "state/form/selectors"
import Heading from "app/components/Heading"
import { useTranslate } from "hooks/translate"
import { getAppLocale } from "state/app/selectors"
import { useAppflow } from "app/hooks/useAppFlow"
import { CarEnum } from "app/pages/appFlow/types"
import { StyledIframe } from "../../style"

const INTERMEDIARY_NAME = "Polestar"
const INTERMEDIARY_TYPE_CODE = "0288"
// KBC only accepts three purpose codes for intermediaryTypeCode 0288: 82111
// (new EV), 82112 (used EV up to 3 years) and 82113 (used EV older than 3
// years). Anything else is rejected with CO2106 "Bemiddelaar niet toegelaten
// voor gekozen doel of vennootschap" and the widget shows its error screen.
const CREDIT_PURPOSE_CODE_NEW = "82111"
const CREDIT_PURPOSE_CODE_USED_RECENT = "82112"
const CREDIT_PURPOSE_CODE_USED_OLD = "82113"
const USED_CAR_RECENT_MAX_AGE = 3
const HIDE_REQUEST_LOAN_BUTTON = false
const IS_CREDIT_AMOUNT_EDITABLE = true
const IS_LOGO_SHOWN = true
const COMPANY = "0001"

// Step 2 only asks for the year of first registration, so the three-year
// boundary between 82112 and 82113 is evaluated in whole years. Falls back to
// the older-car code when the year is missing, which can only happen when
// someone lands on this step directly.
const getUsedCarPurposeCode = (constructionYear?: string): string => {
  const year = Number(constructionYear)
  if (!year) return CREDIT_PURPOSE_CODE_USED_OLD

  const age = new Date().getFullYear() - year
  return age <= USED_CAR_RECENT_MAX_AGE
    ? CREDIT_PURPOSE_CODE_USED_RECENT
    : CREDIT_PURPOSE_CODE_USED_OLD
}

const Step4: React.FC = () => {
  const appLocale = useSelector(getAppLocale)
  const { t } = useTranslate()
  const { carType } = useAppflow()
  const CONSTRUCTION_YEAR = useSelector(
    getFormFieldData("constructionYear")
  ) as string | undefined
  const CREDIT_PURPOSE_CODE =
    carType === CarEnum.New
      ? CREDIT_PURPOSE_CODE_NEW
      : getUsedCarPurposeCode(CONSTRUCTION_YEAR)
  const CREDIT_AMOUNT = useSelector(
    getFormFieldData("desiredFinancingAmount")
  ) as string
  // Mandatory since KBC added the balloon option (LOA+): the residual value is
  // calculated on purchaseAmount, and without it the widget fails to render.
  // This funnel only asks for the amount to finance, so we mirror it here.
  const PURCHASE_AMOUNT = CREDIT_AMOUNT

  useEffect(() => {
    function receiveKBCMessage(event: MessageEvent) {
      if (event.data.caller == "LOAWidget") {
        window.open(event.data.url)
      }
    }
    window.addEventListener("message", receiveKBCMessage, false)

    return () => {
      window.removeEventListener("message", receiveKBCMessage, false)
    }
  })

  return (
    <>
      <Heading>{t("system.product.loa.step4.title")}</Heading>
      <p>{t("system.product.loa.step4.description")}</p>
      <script
        type="text/javascript"
        src={`https://www.kbc.be/GA0/carwidget/resources/${COMPANY}/en/assets/kbc-car-insurance-widget.js`}
      />
      <StyledIframe
        className="kbc-loa-widget-iframe"
        style={{ border: "none", maxWidth: "800px" }}
        height="850"
        width="550"
        src={`https://www.kbc.be/co2-p-wgt/visual-widget/resources/${COMPANY}/${appLocale}/?creditAmount=${CREDIT_AMOUNT}&purchaseAmount=${PURCHASE_AMOUNT}&intermediaryName=${INTERMEDIARY_NAME}&intermediaryTypeCode=${INTERMEDIARY_TYPE_CODE}&creditPurposeCode=${CREDIT_PURPOSE_CODE}&hideRequestLoanButton=${HIDE_REQUEST_LOAN_BUTTON}&isCreditAmountEditable=${IS_CREDIT_AMOUNT_EDITABLE}&isLogoShown=${IS_LOGO_SHOWN}#/loa/simulation`}
      >
        <p>Your browser does not support iframes.</p>
      </StyledIframe>
    </>
  )
}

export default Step4
